import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Play } from "lucide-react";

const COOKIE_KEY = "pg_cookie_consent_v1";

/**
 * Floating "Try interactive demo" pill that appears after the user scrolls past
 * the hero and hides when the footer is in view. Desktop-first; on mobile it
 * shrinks into an icon + short label to avoid overlapping form CTAs.
 *
 * Suppressed while the cookie banner is still open (no consent choice yet)
 * to avoid stacking two bottom-right floats.
 */
export default function StickyDemoCta() {
    const [show, setShow] = useState(false);
    const [cookieResolved, setCookieResolved] = useState(false);

    useEffect(() => {
        const resolve = () => {
            try {
                const v = localStorage.getItem(COOKIE_KEY);
                setCookieResolved(v === "accepted" || v === "rejected");
            } catch {
                setCookieResolved(true);
            }
        };
        resolve();
        const id = setInterval(resolve, 600);
        const onStorage = (e) => { if (e.key === COOKIE_KEY) resolve(); };
        window.addEventListener("storage", onStorage);
        return () => { clearInterval(id); window.removeEventListener("storage", onStorage); };
    }, []);

    useEffect(() => {
        const SHOW_AFTER = 700;
        const onScroll = () => {
            const scrolled = window.scrollY > SHOW_AFTER;
            const nearBottom =
                window.innerHeight + window.scrollY >=
                document.documentElement.scrollHeight - 320;
            setShow(scrolled && !nearBottom);
        };
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", onScroll);
        return () => {
            window.removeEventListener("scroll", onScroll);
            window.removeEventListener("resize", onScroll);
        };
    }, []);

    const visible = show && cookieResolved;

    return (
        <div
            data-testid="sticky-demo-cta"
            aria-hidden={!visible}
            className={`fixed z-40 bottom-5 right-5 sm:bottom-6 sm:right-6 transition-all duration-300 ${
                visible
                    ? "opacity-100 translate-y-0 pointer-events-auto"
                    : "opacity-0 translate-y-3 pointer-events-none"
            }`}
        >
            <Link
                to="/demo"
                data-testid="sticky-demo-trigger"
                className="group inline-flex items-center gap-2 rounded-full bg-brand-primary hover:bg-brand-hover text-white shadow-[0_10px_30px_-8px_rgba(14,78,90,0.55)] ring-1 ring-brand-tint0/20 px-4 sm:px-5 h-12 text-sm font-semibold tracking-tight transition-all hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-10px_rgba(14,78,90,0.65)]"
            >
                <Play className="h-4 w-4" aria-hidden />
                <span className="hidden sm:inline">Try interactive demo</span>
                <span className="sm:hidden font-semibold">Try demo</span>
            </Link>
        </div>
    );
}

